<script setup lang="ts">
/**
 * L'onglet « Surveillance » de la fiche d'une source ou d'un bâtiment (écran 1
 * du dossier, filtré sur l'équipement), en lecture.
 *
 * Ce que l'utilisateur doit vivre : depuis la fiche d'une sonde, il lit les
 * surveillances de cet équipement comme dans la liste, et ouvre l'une d'elles
 * par le nom de l'équipement. Un refus ou une panne se dit à la place de la
 * liste, jamais par une bannière. Hors de l'offre Energy Manager, `CadreModule`
 * rend l'écran hors offre et rien n'est lu.
 */
// ================================
// IMPORTS
// ================================
import { computed, watch } from "vue"
import { useI18n } from "vue-i18n"
import { useAuthStore } from "@core/stores/auth.store"
import { useEquipementStore } from "@modules/alarmes-v2/stores/equipement.store"
import { useTypesStore } from "@modules/alarmes-v2/stores/types.store"
import { cleDuRefus } from "@modules/alarmes-v2/utils/refus"

// ================================
// INTERNATIONALIZATION
// ================================
const { t } = useI18n()

// ================================
// PROPS & EMITS
// ================================
const props = defineProps<{
    /** Le genre de fiche : une source ou un bâtiment. */
    objetType: "source" | "building"
    /** L'identifiant de l'équipement, lu dans l'adresse. */
    idObjet: string
}>()

/**
 * La session connectée.
 */
const authStore = useAuthStore()

/**
 * Les surveillances de l'équipement.
 */
const equipementStore = useEquipementStore()

/**
 * Le catalogue, pour le nom des types.
 */
const typesStore = useTypesStore()

/**
 * Ce que la page montre à la place de la liste, ou `liste`.
 */
const getVue = computed((): "chargement" | "erreur" | "vide" | "liste" => {
    if (equipementStore.statut === "erreur") return "erreur"
    if (equipementStore.surveillances.length > 0) return "liste"
    if (equipementStore.statut === "pret" && !equipementStore.suiteDisponible)
        return "vide"
    return equipementStore.statut === "pret" ? "liste" : "chargement"
})

/**
 * La phrase du refus ou de l'indisponibilité, à la place de la liste.
 */
const getPhraseErreur = computed((): string => {
    const erreur = equipementStore.erreur
    return t(erreur ? cleDuRefus(erreur) : "alarmesV2.refus.indisponible")
})

/**
 * Les alarmes ouvertes, par surveillance.
 */
const getAlarmes = computed(() =>
    Object.fromEntries(
        equipementStore.surveillances
            .map((s) => [s._id, equipementStore.alarmeDe(s._id)] as const)
            .filter(([, a]) => a !== null)
    )
)

/**
 * Lit les surveillances de l'équipement, si l'entreprise a l'offre.
 */
function lire(): void {
    if (!authStore.hasOfferEnergyManager) return
    void equipementStore.charger({
        objetType: props.objetType,
        idObjet: props.idObjet,
    })
    void typesStore.charger()
}

// Relue à chaque entrée, quand l'équipement, l'entreprise ou l'offre change.
watch(
    () => [
        props.objetType,
        props.idObjet,
        authStore.companyId,
        authStore.hasOfferEnergyManager,
    ],
    lire,
    { immediate: true }
)

/**
 * Relit les alarmes de la liste après un acquittement (lot F6), sans la
 * vider.
 * Vrai si la relecture a abouti.
 */
async function relireLaListe(): Promise<boolean> {
    if (!authStore.hasOfferEnergyManager) return false
    return equipementStore.relireLesAlarmes()
}

/**
 * Relit la liste après un échec.
 */
function onRelire(): void {
    equipementStore.reinitialiser()
    lire()
}

/**
 * Reprend la lecture après le plafond de pages vides.
 */
function onAfficherLaSuite(): void {
    void equipementStore.afficherLaSuite()
}
</script>

<template>
    <!-- ================================ -->
    <!-- COMPOSANT PRINCIPAL -->
    <!-- ================================ -->
    <CadreModule>
        <div class="flex flex-col gap-4 p-4 md:p-6">
            <!-- ================================ -->
            <!-- EN-TÊTE -->
            <!-- ================================ -->
            <header>
                <h1 class="text-2xl font-semibold text-surface-900">
                    {{ t("alarmesV2.titre") }}
                </h1>
                <p class="mt-1 text-sm text-surface-700">
                    {{ t("alarmesV2.equipement.intro") }}
                </p>
            </header>

            <!-- ================================ -->
            <!-- CONTENU PRINCIPAL -->
            <!-- ================================ -->
            <section class="rounded-lg border border-surface-200 bg-surface-0">
                <p
                    v-if="getVue === 'chargement'"
                    class="px-4 py-6 text-sm text-surface-600"
                >
                    {{ t("alarmesV2.liste.chargement") }}
                </p>
                <div
                    v-else-if="getVue === 'erreur'"
                    class="flex flex-wrap items-center gap-3 px-4 py-6"
                    data-test="erreur"
                >
                    <p class="text-sm text-surface-800">
                        {{ getPhraseErreur }}
                    </p>
                    <button
                        type="button"
                        class="text-sm font-medium text-primary-700 underline"
                        @click="onRelire"
                    >
                        {{ t("alarmesV2.refus.relire") }}
                    </button>
                </div>
                <p
                    v-else-if="getVue === 'vide'"
                    class="px-4 py-6 text-sm text-surface-600"
                    data-test="vide"
                >
                    {{ t("alarmesV2.equipement.vide") }}
                </p>
                <ListeSurveillances
                    v-else
                    :surveillances="equipementStore.surveillances"
                    :alarmes="getAlarmes"
                    :personnes="equipementStore.personnes"
                    :relire="relireLaListe"
                />

                <div
                    v-if="equipementStore.suiteDisponible"
                    class="flex flex-wrap items-center gap-3 border-t border-surface-200 px-4 py-3"
                >
                    <span class="text-xs text-surface-600">{{
                        t("alarmesV2.liste.suiteAide")
                    }}</span>
                    <button
                        type="button"
                        class="text-sm font-medium text-primary-700 underline"
                        data-test="suite"
                        @click="onAfficherLaSuite"
                    >
                        {{ t("alarmesV2.liste.suite") }}
                    </button>
                </div>
            </section>
        </div>
    </CadreModule>
</template>
